import { useState, useEffect, useRef } from 'react';
import { X, Crown, Rocket, Satellite, Lock, Check, Loader2, AlertCircle, QrCode, ExternalLink, LogIn } from 'lucide-react';
import { useProgress } from '../hooks/useProgress';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const PREMIUM_PRICE = 9.99;
const QR_CODE_URL = 'https://customer-assets.emergentagent.com/job_3aecd162-e766-4a99-bbcc-27cd7262e019/artifacts/4auenbuf_checkout-link-qr-code.png';

const PREMIUM_BENEFITS = [
  { icon: Rocket, text: 'Advanced Orbits (Molniya, Tundra, Lagrange, etc.)' },
  { icon: Satellite, text: 'TLE Parser - Visualize real satellite orbits' },
  { icon: Lock, text: 'Camera Lock - Follow satellites in 3D' },
  { icon: Check, text: 'Editable Orbital Data' },
  { icon: Check, text: 'Lifetime Access - One-time payment' },
];

export function PremiumModal({ isOpen, onClose }) {
  const { setPremium } = useProgress();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const pollRef = useRef(null);

  // Clean up polling on unmount
  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  // Poll premium status after payment initiated
  const startPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    let pollCount = 0;
    pollRef.current = setInterval(async () => {
      try {
        // First check if already marked premium
        const checkRes = await fetch(`${API_URL}/api/premium/check`, { credentials: 'include' });
        if (checkRes.ok) {
          const checkData = await checkRes.json();
          if (checkData.is_premium) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setPaymentSuccess(true);
            setPremium(true);
            setTimeout(() => { window.location.href = '/learn'; }, 2000);
            return;
          }
        }
        // Every 3rd poll, actively verify with Square API
        pollCount++;
        if (pollCount % 3 === 0) {
          const verifyRes = await fetch(`${API_URL}/api/premium/verify-payment`, {
            method: 'POST', credentials: 'include',
          });
          if (verifyRes.ok) {
            const verifyData = await verifyRes.json();
            if (verifyData.is_premium) {
              clearInterval(pollRef.current);
              pollRef.current = null;
              setPaymentSuccess(true);
              setPremium(true);
              setTimeout(() => { window.location.href = '/learn'; }, 2000);
              return;
            }
          }
        }
      } catch { /* ignore */ }
    }, 4000);
  };

  const handlePayNow = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/premium/create-checkout`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Could not create checkout');
      }
      const data = await res.json();

      // Open checkout in popup window
      const w = 500, h = window.innerHeight * 0.75;
      const left = (window.innerWidth - w) / 2 + window.screenLeft;
      const top = (window.innerHeight - h) / 2 + window.screenTop;
      window.open(data.checkout_url, 'Square Payment', `scrollbars=yes,width=${w},height=${h},top=${top},left=${left}`);

      // Start polling for completion
      startPolling();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyPayment = async () => {
    setVerifying(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/premium/verify-payment`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (data.is_premium) {
        setPaymentSuccess(true);
        setPremium(true);
        setTimeout(() => { window.location.href = '/learn'; }, 2000);
      } else {
        setError('Payment not found yet. It may take a moment to process.');
      }
    } catch {
      setError('Verification failed. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  const handleSignIn = () => {
    onClose();
    navigate('/login');
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)' }}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md mx-4 p-6 rounded-2xl animate-fadeIn"
        style={{ background: 'linear-gradient(180deg, rgba(20,25,35,0.98) 0%, rgba(10,12,18,0.98) 100%)', border: '1px solid rgba(255,215,0,0.3)', boxShadow: '0 0 60px rgba(255,215,0,0.1)', maxHeight: '90vh', overflowY: 'auto' }}
        onClick={e => e.stopPropagation()}
        data-testid="premium-modal"
      >
        {/* Close button */}
        <button onClick={onClose} data-testid="premium-modal-close"
          className="absolute top-4 right-4 p-2 rounded-full"
          style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', border: 'none' }}>
          <X size={18} />
        </button>

        {paymentSuccess ? (
          <div className="text-center py-8">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-4" style={{ background: 'rgba(0,255,148,0.15)', border: '2px solid rgba(0,255,148,0.5)' }}>
              <Check size={40} color="#00FF94" />
            </div>
            <h2 style={{ fontFamily: 'Rajdhani', fontSize: '28px', fontWeight: 700, color: '#00FF94', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '8px' }}>
              Payment Successful!
            </h2>
            <p style={{ fontFamily: 'Outfit', fontSize: '15px', color: 'rgba(255,255,255,0.6)' }}>
              Premium unlocked. Redirecting...
            </p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ background: 'linear-gradient(135deg, rgba(255,215,0,0.2), rgba(255,150,0,0.1))', border: '2px solid rgba(255,215,0,0.5)' }}>
                <Crown size={32} color="#FFD700" />
              </div>
              <h2 style={{ fontFamily: 'Rajdhani', fontSize: '28px', fontWeight: 700, color: '#FFD700', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '8px' }}>
                Go Premium
              </h2>
              <p style={{ fontFamily: 'Outfit', fontSize: '15px', color: 'rgba(255,255,255,0.6)', lineHeight: 1.6 }}>
                Unlock the full universe of orbital mechanics
              </p>
            </div>

            {!showPayment ? (
              <>
                {/* Benefits */}
                <div className="space-y-3 mb-6">
                  {PREMIUM_BENEFITS.map((benefit, i) => (
                    <div key={i} className="flex items-center gap-3 px-4 py-2 rounded-lg" style={{ background: 'rgba(255,215,0,0.03)' }}>
                      <benefit.icon size={18} color="#FFD700" />
                      <span style={{ fontFamily: 'Outfit', fontSize: '14px', color: 'rgba(255,255,255,0.8)' }}>{benefit.text}</span>
                    </div>
                  ))}
                </div>

                {/* Price */}
                <div className="text-center mb-6 py-4 rounded-xl" style={{ background: 'rgba(255,215,0,0.05)', border: '1px solid rgba(255,215,0,0.15)' }}>
                  <p style={{ fontFamily: 'JetBrains Mono', fontSize: '11px', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.1em', marginBottom: '4px' }}>LIFETIME ACCESS</p>
                  <div className="flex items-baseline justify-center gap-1">
                    <span style={{ fontFamily: 'Rajdhani', fontSize: '48px', fontWeight: 700, color: '#fff' }}>${Math.floor(PREMIUM_PRICE)}</span>
                    <span style={{ fontFamily: 'Rajdhani', fontSize: '24px', fontWeight: 600, color: 'rgba(255,255,255,0.5)' }}>.{Math.round((PREMIUM_PRICE % 1) * 100).toString().padStart(2, '0')}</span>
                  </div>
                  <p style={{ fontFamily: 'JetBrains Mono', fontSize: '10px', color: 'rgba(255,255,255,0.3)', marginTop: '4px' }}>One-time payment &middot; No subscription &middot; AUD</p>
                </div>

                {/* CTA Button */}
                <button
                  onClick={() => user ? setShowPayment(true) : handleSignIn()}
                  data-testid="upgrade-btn"
                  className="w-full py-4 rounded-xl flex items-center justify-center gap-3"
                  style={{
                    background: 'linear-gradient(135deg, #FFD700, #FF9500)',
                    border: 'none', cursor: 'pointer', fontFamily: 'Rajdhani', fontWeight: 700,
                    fontSize: '16px', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#000',
                    boxShadow: '0 4px 20px rgba(255,215,0,0.3)', transition: 'transform 0.2s, box-shadow 0.2s',
                  }}
                >
                  {user ? <Crown size={20} /> : <LogIn size={20} />}
                  {user ? 'Upgrade Now' : 'Sign In to Upgrade'}
                </button>

                <p className="text-center mt-4" style={{ fontFamily: 'JetBrains Mono', fontSize: '10px', color: 'rgba(255,255,255,0.3)' }}>
                  Secure payment via Square
                </p>
              </>
            ) : (
              <>
                {/* Back button */}
                <button onClick={() => { setShowPayment(false); setError(null); }} data-testid="back-to-benefits"
                  className="mb-4 flex items-center gap-1"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'JetBrains Mono', fontSize: '11px', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.08em' }}>
                  &larr; BACK
                </button>

                {/* QR Code */}
                <div className="text-center mb-5">
                  <div className="inline-block p-3 rounded-xl mb-3" style={{ background: '#fff' }}>
                    <img src={QR_CODE_URL} alt="Scan to pay" style={{ width: '160px', height: '160px', display: 'block' }} />
                  </div>
                  <p style={{ fontFamily: 'JetBrains Mono', fontSize: '10px', color: 'rgba(255,255,255,0.35)', letterSpacing: '0.08em' }}>
                    SCAN WITH PHONE TO PAY
                  </p>
                </div>

                {/* Divider */}
                <div className="flex items-center gap-3 mb-5">
                  <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.1)' }} />
                  <span style={{ fontFamily: 'JetBrains Mono', fontSize: '10px', color: 'rgba(255,255,255,0.3)', letterSpacing: '0.1em' }}>OR</span>
                  <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.1)' }} />
                </div>

                {/* Pay Now Button */}
                <button
                  onClick={handlePayNow}
                  disabled={loading}
                  data-testid="pay-now-btn"
                  className="w-full py-4 rounded-xl flex items-center justify-center gap-3 mb-3"
                  style={{
                    background: 'linear-gradient(135deg, #FFD700, #FF9500)',
                    border: 'none', cursor: loading ? 'wait' : 'pointer', fontFamily: 'Rajdhani', fontWeight: 700,
                    fontSize: '16px', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#000',
                    boxShadow: '0 4px 20px rgba(255,215,0,0.3)', opacity: loading ? 0.7 : 1,
                  }}
                >
                  {loading ? <Loader2 size={20} className="animate-spin" /> : <ExternalLink size={18} />}
                  {loading ? 'Opening checkout...' : `Pay A$${PREMIUM_PRICE} Now`}
                </button>

                {/* Price reminder */}
                <div className="text-center mb-4 py-2 rounded-lg" style={{ background: 'rgba(255,215,0,0.05)', border: '1px solid rgba(255,215,0,0.1)' }}>
                  <p style={{ fontFamily: 'JetBrains Mono', fontSize: '9px', color: 'rgba(255,255,255,0.3)', letterSpacing: '0.1em' }}>
                    LIFETIME ACCESS &middot; A${PREMIUM_PRICE} &middot; NO SUBSCRIPTION
                  </p>
                </div>

                {/* Already paid? Verify */}
                <button
                  onClick={handleVerifyPayment}
                  disabled={verifying}
                  data-testid="verify-payment-btn"
                  className="w-full py-2.5 rounded-lg flex items-center justify-center gap-2"
                  style={{
                    background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)',
                    cursor: verifying ? 'wait' : 'pointer', fontFamily: 'Rajdhani', fontWeight: 600,
                    fontSize: '13px', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.08em',
                  }}
                >
                  {verifying ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                  {verifying ? 'Checking...' : "I've already paid"}
                </button>

                {/* Error */}
                {error && (
                  <div className="flex items-center gap-2 mt-3 p-3 rounded-lg" style={{ background: 'rgba(255,68,68,0.1)', border: '1px solid rgba(255,68,68,0.3)' }}>
                    <AlertCircle size={16} color="#FF4444" />
                    <p style={{ fontFamily: 'JetBrains Mono', fontSize: '11px', color: '#FF4444' }}>{error}</p>
                  </div>
                )}

                {/* Polling indicator */}
                {pollRef.current && (
                  <div className="flex items-center justify-center gap-2 mt-3 py-2">
                    <Loader2 size={14} className="animate-spin" color="#FFD700" />
                    <span style={{ fontFamily: 'JetBrains Mono', fontSize: '10px', color: 'rgba(255,215,0,0.6)' }}>
                      Waiting for payment confirmation...
                    </span>
                  </div>
                )}

                <p className="text-center mt-4" style={{ fontFamily: 'JetBrains Mono', fontSize: '10px', color: 'rgba(255,255,255,0.3)' }}>
                  Powered by Square &middot; PCI compliant
                </p>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Small badge to show in navigation or locked content
export function PremiumBadge({ onClick }) {
  return (
    <button onClick={onClick} data-testid="premium-badge"
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full"
      style={{ background: 'linear-gradient(135deg, rgba(255,215,0,0.15), rgba(255,150,0,0.1))', border: '1px solid rgba(255,215,0,0.4)', cursor: 'pointer', transition: 'transform 0.2s' }}>
      <Crown size={14} color="#FFD700" />
      <span style={{ fontFamily: 'Rajdhani', fontWeight: 600, fontSize: '12px', color: '#FFD700', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Premium</span>
    </button>
  );
}

// Lock overlay for premium content
export function PremiumLock({ onUpgrade, feature = 'this feature' }) {
  return (
    <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
      <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ background: 'rgba(255,215,0,0.1)', border: '2px solid rgba(255,215,0,0.3)' }}>
        <Lock size={28} color="rgba(255,215,0,0.7)" />
      </div>
      <h3 style={{ fontFamily: 'Rajdhani', fontSize: '20px', fontWeight: 700, color: 'rgba(255,255,255,0.8)', marginBottom: '8px' }}>
        Premium Feature
      </h3>
      <p style={{ fontFamily: 'Outfit', fontSize: '14px', color: 'rgba(255,255,255,0.5)', marginBottom: '16px', maxWidth: '280px' }}>
        Upgrade to Premium to unlock {feature} and all advanced content.
      </p>
      <button onClick={onUpgrade}
        className="flex items-center gap-2 px-5 py-2.5 rounded-lg"
        style={{ background: 'linear-gradient(135deg, rgba(255,215,0,0.2), rgba(255,150,0,0.15))', border: '1px solid rgba(255,215,0,0.4)', cursor: 'pointer', fontFamily: 'Rajdhani', fontWeight: 600, fontSize: '13px', color: '#FFD700', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
        <Crown size={16} /> Unlock Premium
      </button>
    </div>
  );
}
